import { apiClient } from './client'
import { API_ENDPOINTS } from '@/utils/constants'
import type { UserSettings, UserSettingsUpdate } from '@/types'
import type { AxiosError } from 'axios'

interface SettingsApiResponse {
  success?: boolean
  message?: string
  data?: UserSettings
}

export type StudentSettingsRestrictionReason = 'subscription_expired' | 'not_in_plan'

interface StudentSettingsApiResponse extends SettingsApiResponse {
  can_edit_individual_settings?: boolean
  student_settings_restriction_reason?: StudentSettingsRestrictionReason | null
}

export interface StudentSettingsResult {
  settings: UserSettings
  canEditIndividualSettings: boolean
  restrictionReason: StudentSettingsRestrictionReason | null
}

export const userSettingsApi = {
  /**
   * Получить настройки текущего пользователя. GET /user-settings
   */
  getMySettings: async (): Promise<UserSettings> => {
    try {
      const response = await apiClient.get<SettingsApiResponse>(API_ENDPOINTS.USER_SETTINGS)
      if (response.data?.data) {
        return response.data.data
      }
      throw new Error(response.data?.message || 'Ошибка загрузки настроек')
    } catch (error) {
      // Извлекаем сообщение об ошибке из ответа API
      const axiosError = error as AxiosError<{ message?: string; error?: string }>
      const errorMessage =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        axiosError.message ||
        'Ошибка загрузки настроек'
      throw new Error(errorMessage)
    }
  },

  /**
   * Обновить настройки текущего пользователя. PUT /user-settings
   */
  updateMySettings: async (data: UserSettingsUpdate): Promise<UserSettings> => {
    try {
      const response = await apiClient.put<SettingsApiResponse>(API_ENDPOINTS.USER_SETTINGS, data)
      if (response.data?.data) {
        return response.data.data
      }
      throw new Error(response.data?.message || 'Ошибка сохранения настроек')
    } catch (error) {
      // Извлекаем сообщение об ошибке из ответа API
      const axiosError = error as AxiosError<{ message?: string; error?: string }>
      const errorMessage =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        axiosError.message ||
        'Ошибка сохранения настроек'
      throw new Error(errorMessage)
    }
  },

  /**
   * Получить настройки ученика (для учителя). GET /users/{user}/user-settings
   * Возвращает настройки и флаги доступа к редактированию индивидуальных настроек.
   */
  getUserSettings: async (userId: number): Promise<StudentSettingsResult> => {
    try {
      const response = await apiClient.get<StudentSettingsApiResponse>(
        `${API_ENDPOINTS.USERS}/${userId}/user-settings`
      )
      const data = response.data
      if (data?.data) {
        return {
          settings: data.data,
          canEditIndividualSettings: data.can_edit_individual_settings ?? true,
          restrictionReason: data.student_settings_restriction_reason ?? null,
        }
      }
      throw new Error(data?.message || 'Ошибка загрузки настроек ученика')
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string; error?: string }>
      const errorMessage =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        axiosError.message ||
        'Ошибка загрузки настроек ученика'
      throw new Error(errorMessage)
    }
  },

  /**
   * Обновить настройки ученика (для учителя). PUT /users/{user}/user-settings
   */
  updateUserSettings: async (
    userId: number,
    data: UserSettingsUpdate
  ): Promise<UserSettings> => {
    try {
      const response = await apiClient.put<SettingsApiResponse>(
        `${API_ENDPOINTS.USERS}/${userId}/user-settings`,
        data
      )
      if (response.data?.data) {
        return response.data.data
      }
      throw new Error(response.data?.message || 'Ошибка сохранения настроек ученика')
    } catch (error) {
      // Извлекаем сообщение об ошибке из ответа API
      const axiosError = error as AxiosError<{ message?: string; error?: string }>
      const errorMessage =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        axiosError.message ||
        'Ошибка сохранения настроек ученика'
      throw new Error(errorMessage)
    }
  },
}
